import React from 'react';
import styled from 'styled-components';
import ReportHeader from './ReportHeader';
import ReportBudget from './ReportBudget';
import LegacyReportInfo from './LegacyReportInfo';
import ReportNarrative from './ReportNarrative';
import ReportDescription from './ReportDescription';
import ReportProgramDetails from './ReportProgramDetails';
import ReportImpactSummaryTable from './ReportImpactSummary';
import ReportImpactSummaryTableForLegacy from './ReportImpactSummary/ReportImpactSummaryTableForLegacy';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useReactToPrint } from 'react-to-print';
import { ReportAnswers } from '../FeedbackReport';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import ReportBeneficiaries from './ReportBeneficiaries';
import ReportActivities from './ReportActivities';
import ReportSectionNote from './ReportSectionNote';
import ReportCustomQuestions from './ReportCustomQuestions';
import { NOTE_QUESTION_IDS } from 'npo/pages/ReportBuilder/constants';
import {
  ImpactReceipt,
  Report as ReportType,
} from '../../../DonorReceipt/utils';

const StyledImpactDiv = styled.div`
  background-color: ${COLORS.whiteBase};
  padding: ${SPACERS.medium} ${SPACERS.extraLarge};
`;

export const ImpactModelHeading: React.FC = () => (
  <StyledImpactDiv>
    <TrueImpactText variant="heading-subheading">Impact Model</TrueImpactText>
    <TrueImpactText variant="body-small">
      Below is the grantee&apos;s summary of how this program/initiative
      generates value, including the criteria for success at each stage and the
      number of successes achieved (Indicator), where the data come from
      (Measurement), and, if relevant, how much the current numbers vary from
      the original forecast before implementation (Variance).
    </TrueImpactText>
  </StyledImpactDiv>
);

interface FeedbackImpactReceiptProps {
  isThirdParty: boolean;
  reportId: number | string;
  programName: string | undefined;
  hideHeader?: boolean;
  answers: ReportAnswers;
  component?: React.ReactNode;
  feedback: FeedbackDashboardCardProps;
  className?: string;
  isLegacy?: boolean;
  report?: ReportType;
  ir?: ImpactReceipt;
}

const StyledDiv = styled.div``;

const FeedbackImpactReceipt: React.FC<FeedbackImpactReceiptProps> = ({
  isThirdParty,
  reportId,
  answers,
  feedback,
  component,
  hideHeader,
  programName,
  className,
  isLegacy,
  report,
  ir,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const handlePrint: () => void = hideHeader
    ? (): void => undefined
    : useReactToPrint({ content: (): HTMLDivElement | null => ref.current });

  const name =
    programName || feedback.title || feedback.portfolioName || report?.name;

  return (
    <StyledDiv ref={ref} className={className}>
      {!hideHeader && (
        <ReportHeader
          heading="IMPACT REPORT"
          handlePrint={handlePrint}
          isThirdParty={isThirdParty}
          showBackButton={isThirdParty}
        />
      )}
      <div className="impact-receipt-report-content">
        <ReportProgramDetails
          programName={name}
          isThirdParty={isThirdParty}
          organizationName={feedback.organizationName}
        />
        <ReportDescription
          answers={answers}
          defaultDescription={isLegacy ? report?.description : undefined}
          legacyIr={{
            startDate: report?.startDate,
            endDate: report?.endDate,
            location: report?.location,
          }}
        />
        <ReportActivities answers={answers} />
        {component}
        <ImpactModelHeading />
        {isLegacy && ir?.legacy?.impactModel ? (
          <ReportImpactSummaryTableForLegacy
            impactModel={ir.legacy.impactModel}
          />
        ) : (
          <ReportImpactSummaryTable
            reportId={reportId}
            programName={name}
            answers={answers}
            isLegacy={isLegacy || false}
          />
        )}
        <ReportSectionNote
          answers={answers}
          questionId={NOTE_QUESTION_IDS.impactDetails}
          label="Impact Model notes:"
        />
        <ReportBeneficiaries
          answers={answers}
          isLegacy={isLegacy}
          legacyDemographics={ir?.legacy?.demographics}
        />
        <ReportNarrative
          answers={answers}
          isLegacy={isLegacy}
          report={report}
        />
        <ReportBudget
          isLegacy={isLegacy}
          reportId={reportId}
          answers={answers}
          budget={report?.budget}
        />
        <ReportCustomQuestions reportId={reportId} answers={answers} />
        {isLegacy && <LegacyReportInfo report={report} />}
      </div>
    </StyledDiv>
  );
};

export default FeedbackImpactReceipt;
